import React from 'react';
import { InvoiceModel } from '../../../helpers';
import { IInvoicePaymentScheduleEntity } from '../../../../../actions/DataServiceEntities.g';
import { ICultureInfoFormatter } from '@msdyn365-commerce/core';
import { getInvoiceDetailsPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { getInvoiceStatusText, InvoicePaidStatus } from '../../../helpers/invoice-status';
import { IGdnInvoicesListResources } from '../../../gdn-invoices-list.props.autogenerated';
import { InvoiceType } from '@msdyn365-commerce/retail-proxy';

interface InvoiceTileProps {
    invoice: InvoiceModel;
    invoiceBreakdownList: IInvoicePaymentScheduleEntity[];
    cultureFormatter: ICultureInfoFormatter;
    addInvoiceToCart: (
        invoiceId: string,
        amount: number,
        allowableFreightAmount: number,
        isAllowableRowSelected: boolean,
        amountDue: number
    ) => void;
    actionContext: any;
    resources: IGdnInvoicesListResources;
    cart?: any;
    //addAllowableFreightAmountToCart: (invoiceId: string, amount: number, isSelected: boolean) => void;
    allowMultipleInvoicePayment?: boolean;
    paymentAddedMessage?: string;
    paymentPendingMessage: string;
}

export const InvoiceTile: React.FC<InvoiceTileProps> = (props: InvoiceTileProps) => {
    const {
        invoice,
        invoiceBreakdownList,
        cultureFormatter: { formatCurrency, formatDate },
        addInvoiceToCart,
        actionContext,
        resources,
        cart,
        // addAllowableFreightAmountToCart,
        allowMultipleInvoicePayment,
        paymentAddedMessage,
        paymentPendingMessage
    } = props;

    const breakdown = invoiceBreakdownList
        .filter(inv => inv.Invoice === invoice.id)
        .sort((a, b) => new Date(a.DueDate ?? 0).getTime() - new Date(b.DueDate ?? 0).getTime());

    const calculateTotalAmount = (rows: boolean[]): number => {
        const containsValidCashDiscount = breakdown.some(br => !isPastDue(br.CashDiscDate));
        if (!rows.some(row => row)) {
            const totalInvoiceAmount = breakdown.reduce(
                (sum, item) => sum + ((item.AmountCur || 0) - (containsValidCashDiscount ? item.PossibleCashDisc || 0 : 0)),
                0
            );
            const allowableFreightCharge = getAllowableFreightAmount();

            if (allowMultipleInvoicePayment) {
                return totalInvoiceAmount - allowableFreightCharge;
            } else {
                return (
                    (breakdown[0]?.AmountCur || 0) -
                    allowableFreightCharge -
                    (containsValidCashDiscount ? breakdown[0]?.PossibleCashDisc || 0 : 0)
                );
            }
        }

        const selectedAmount = rows.reduce((total, isSelected, idx) => {
            if (idx < breakdown.length) {
                return isSelected
                    ? total + (breakdown[idx].AmountCur || 0) - (containsValidCashDiscount ? breakdown[idx]?.PossibleCashDisc || 0 : 0)
                    : total;
            }
            return total;
        }, 0);

        const allowableFreightCharge = getAllowableFreightAmount();

        if (selectedAmount >= allowableFreightCharge && rows[breakdown.length]) {
            return selectedAmount - allowableFreightCharge;
        }

        return selectedAmount;
    };

    const getEarliestInvoiceDue = () =>
        invoiceBreakdownList.reduce((earliest, current) => {
            return earliest.DueDate && current.DueDate && new Date(current.DueDate) < new Date(earliest.DueDate) ? current : earliest;
        }, invoiceBreakdownList[0]);

    const isPastDue = (date?: Date) => {
        const dateToCompare = date || getEarliestInvoiceDue()?.DueDate;
        return dateToCompare && new Date(dateToCompare) < new Date();
    };
    const getAllowableFreightAmount = (): number => {
        const charge = breakdown.find(b => (b.AllowableFreightCharge || 0) > 0)?.AllowableFreightCharge;
        if (!charge) {
            return 0;
        }
        if (isPastDue()) {
            return 0;
        }
        const dateToCompare = getEarliestInvoiceDue()?.DueDate;
        if (dateToCompare) {
            const isBefore15th = new Date() <= new Date(dateToCompare.getFullYear(), dateToCompare?.getMonth(), 15);
            return isBefore15th ? charge : 0;
        }
        return 0;
    };

    const [isCreditNote, setIsCreditNote] = React.useState<boolean>(false);
    const [selectAll, setSelectAll] = React.useState<boolean>(false);
    const [selectedRows, setSelectedRows] = React.useState<boolean[]>(() => {
        if (allowMultipleInvoicePayment) {
            return breakdown.map(br => isPastDue(br.DueDate) ?? false);
        } else {
            const initialSelection = new Array(breakdown.length).fill(false);
            initialSelection.length > 0 && isPastDue(breakdown[0].DueDate) && (initialSelection[0] = true);
            return initialSelection;
        }
    });
    const [totalAmount, setTotalAmount] = React.useState<number | null>(
        calculateTotalAmount(selectedRows) || (invoice.amountDue || 0) - getAllowableFreightAmount() || 0
    );

    const [displayedAmount, setDisplayedAmount] = React.useState<string>(
        selectedRows.some(row => row) ? totalAmount?.toFixed(2) ?? '0.00' : '0.00'
    );

    const getDisplayedAmount = (rows: boolean[]): string => {
        const totalAmount = calculateTotalAmount(rows);
        if (!rows.some(row => row)) {
            return '0.00';
        }
        return totalAmount.toFixed(2);
    };

    const isInvoiceInCart = React.useMemo(() => {
        return cart.CartLines.some((line: any) => line.InvoiceId === invoice.id);
    }, [cart, invoice.id]);

    const linkToInvoiceDetails = getInvoiceDetailsPageUrlSync(invoice.id, actionContext);

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;

        const updatedRows = breakdown.map((_, idx) => {
            if (allowMultipleInvoicePayment) {
                return checked;
            }
            const isFirstInvoice = idx === 0;
            return checked && isFirstInvoice;
        });

        updatedRows[breakdown.length] = checked;

        setSelectAll(checked);
        setSelectedRows(updatedRows);
        setTotalAmount(calculateTotalAmount(updatedRows));
        setDisplayedAmount(getDisplayedAmount(updatedRows));
    };

    const handleRowSelect = (index: number) => {
        const updatedRows = [...selectedRows];
        const isSelected = !updatedRows[index];
        updatedRows[index] = isSelected;
        updatedRows[breakdown.length] = isSelected;

        const allSelected = updatedRows.every(Boolean);
        const noneSelected = updatedRows.every(checked => !checked);
        setSelectAll(allSelected ? true : noneSelected ? false : allSelected);

        setSelectedRows(updatedRows);
        setTotalAmount(calculateTotalAmount(updatedRows));
        setDisplayedAmount(getDisplayedAmount(updatedRows));
    };

    React.useEffect(() => {
        setIsCreditNote(invoice.invoiceTypeValue === InvoiceType.CreditNoteInvoice);
    }, []);

    React.useEffect(() => {
        let newSelections: boolean[] = new Array(breakdown.length).fill(false);
        let updatedSelections: boolean[] = [];
        setSelectedRows(prevSelectedRows => {
            if (allowMultipleInvoicePayment) {
                newSelections = breakdown.map(br => isPastDue(br.DueDate) ?? false);
            } else {
                newSelections.length > 0 && isPastDue(breakdown[0].DueDate) && (newSelections[0] = true);
            }
            updatedSelections = [...prevSelectedRows, ...newSelections.slice(prevSelectedRows.length)];
            return updatedSelections;
        });
        setDisplayedAmount(getDisplayedAmount(updatedSelections));
    }, [invoiceBreakdownList, allowMultipleInvoicePayment]);

    const discount = getAllowableFreightAmount();
    const invoiceBreakdown = invoiceBreakdownList.find(item => item.Invoice === invoice.id);
    const newInvoiceBreakdownList = invoiceBreakdownList.filter(item => item.Invoice === invoice.id);
    let allowableFreight = 0;
    if (newInvoiceBreakdownList.length > 1) {
        allowableFreight = newInvoiceBreakdownList.find(item => (item.AllowableFreightCharge || 0) > 0)?.AllowableFreightCharge || 0;
        if (allowableFreight > 0) {
            invoiceBreakdown && (invoiceBreakdown.AllowableFreightCharge = allowableFreight);
        }
    }
    const handleReset = () => {
        const selectedTotal = calculateTotalAmount(selectedRows);
        setTotalAmount(selectedTotal);
        setDisplayedAmount(getDisplayedAmount(selectedRows));
    };

    const totalPossibleCashDisc = breakdown.reduce((sum, br) => {
        return sum + (br.PossibleCashDisc ?? 0);
    }, 0);
    const containsValidCashDiscount = breakdown.some(br => !isPastDue(br.CashDiscDate)) && totalPossibleCashDisc > 0;
    const possibleCashDiscount = breakdown.reduce((sum, br) => {
        return sum + (br.PossibleCashDisc ?? 0);
    }, 0);

    const cashDiscount = containsValidCashDiscount ? possibleCashDiscount : 0;
    // Ticket 9019
    const totalPayments = breakdown.reduce((sum, br) => sum + (br.AmountCur || 0), 0).toFixed(2);
    const isPaymentPending = ((invoice.amountDue || 0) + cashDiscount).toFixed(2) !== totalPayments;

    const handleButtonDisabled = () => {
        return (
            isInvoiceInCart ||
            invoice.amount! < 0 ||
            invoice.id.startsWith('FTC') ||
            invoice.id.startsWith('CN') ||
            Number(displayedAmount) === 0 ||
            isPaymentPending
        );
    };

    const shouldDisplayMessage = () => {
        return isInvoiceInCart;
    };

    const handleBlur = (value: string) => {
        const num = parseFloat(value.replace('$', ''));
        const cleanNum = isNaN(num) ? '0.00' : num.toFixed(2);
        setDisplayedAmount(cleanNum);
    };

    const InvoicePaidStatusMap = {
        [InvoicePaidStatus.None]: 'None',
        [InvoicePaidStatus.Unpaid]: 'Not Paid',
        [InvoicePaidStatus.PartiallyPaid]: 'Not Paid',
        [InvoicePaidStatus.PastDue]: 'Not Paid',
        [InvoicePaidStatus.Paid]: 'Paid'
    };

    const convertUTCDateToLocalDate = (date: Date) => {
        var newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);

        var offset = date.getTimezoneOffset() / 60;
        var hours = date.getHours();

        newDate.setHours(hours - offset);
        return newDate;
    };

    const shouldSelectAllowableFreightRow = Number(displayedAmount) === Number(calculateTotalAmount(selectedRows).toFixed(2));
    const disableAllowableFreightRow = true;
    let allowableFreightChargeActual: number = 0;

    return (
        <div key={invoice.id} className={'invoice-listview__tile'}>
            <div className={'invoice-listview__tile__header'}>
                <div className='invoice-details'>
                    <div className='row-1'>
                        <div className='id'>
                            <a
                                className={'invoice-listview__tile--link invoice-listview__tile--bold'}
                                target='_blank'
                                rel='noopener noreferrer'
                                href={linkToInvoiceDetails}
                            >
                                {invoice.id}
                            </a>
                        </div>
                        <div className='invoice-date'>
                            <span className={'invoice-listview__tile--bold'}>{resources.invoiceDateText}</span>{' '}
                            {formatDate(invoice.invoiceDate!)}
                        </div>
                        <div className='sales-order'>
                            <span className={'invoice-listview__tile--bold'}>{resources.salesOrderText}</span> {invoiceBreakdown?.SalesId}
                        </div>
                    </div>
                    <div className='row-2'>
                        <div className='amount'>
                            <span className={'invoice-listview__tile--bold'}>{resources.amountText} </span>
                            {formatCurrency(invoice.amount || 0, invoice.currencyCode)}
                        </div>
                        {!isCreditNote && (
                            <>
                                <div className='terms'>
                                    <span className={'invoice-listview__tile--bold'}>{resources.termsText} </span>
                                    {invoiceBreakdown?.Description}
                                </div>
                                <div className='po'>
                                    <span className={'invoice-listview__tile--bold'}>{resources.poText} </span>
                                    {invoiceBreakdown?.PurchaseOrder}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {isCreditNote || invoice.invoiceTypeValue === InvoiceType.FreeTextCreditNoteInvoice ? (
                    <div className='invoice-status'>
                        <div className='status'>
                            <span className={'ml-1 invoice-listview__tile--bold'}>{resources.creditNoteLabel}</span>
                        </div>
                    </div>
                ) : (
                    <div className='invoice-status'>
                        <div className='status'>
                            {invoice.status !== 3 && isPastDue() ? (
                                <span className={'ml-1 invoice-listview__tile--red invoice-listview__tile--bold'}>
                                    {resources.pastDueText}
                                </span>
                            ) : (
                                <span className={'ml-1 invoice-listview__tile--bold'}>
                                    {getInvoiceStatusText(resources, invoice.status)}
                                </span>
                            )}
                        </div>
                        {invoice.status !== 3 && (
                            <div className='due-date'>
                                <span className={'invoice-listview__tile--bold'}>{resources.nextPaymentText} </span>
                                {formatDate(
                                    convertUTCDateToLocalDate(
                                        breakdown.length > 0 ? breakdown[0].DueDate! : invoice.dueDate!
                                    ).toLocaleString()
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {invoice.status !== 3 && !isCreditNote && <div className='header-border' />}

            {/* ticket 8686 */}
            {invoice.status === 3 ? (
                <div className='invoice-listview__tile__invoice-info'>
                    <div className='amount-paid'>
                        <span className={'invoice-listview__tile--bold'}>{resources.paidText} </span>${invoice.amount}
                    </div>
                </div>
            ) : (
                <div className='invoice-listview__tile__invoice-info'>
                    {discount ? (
                        <div className='discount'>
                            <span className={'invoice-listview__tile--bold'}>{resources.discountText}:</span>{' '}
                            {formatCurrency(discount, invoice.currencyCode)}
                        </div>
                    ) : null}
                    {/* {discount > 0 ? (
                        <p>
                            <span className={'invoice-listview__tile--bold'}>Discount Amount: </span>
                            {formatCurrency(discount, invoice.currencyCode)}
                        </p>
                    ) : null} */}
                    <div className='amount-details'>
                        <div className='amount-due'>
                            <span className={'invoice-listview__tile--bold'}>{resources.amountDueText} </span>
                            {formatCurrency(invoice.amountDue || 0, invoice.currencyCode)}
                        </div>
                        <div className='amount-paid'>
                            <span className={'invoice-listview__tile--bold'}>{resources.amountPaidText} </span>
                            {formatCurrency((invoice.amount || 0) - (invoice.amountDue || 0), invoice.currencyCode)}
                        </div>
                    </div>
                    {breakdown.length > 0 ? (
                        <div className={'invoice-listview__tile__breakdown'}>
                            <table className={'invoice-listview__tile__table'}>
                                <thead>
                                    <tr>
                                        <th className='checkbox'>
                                            {allowMultipleInvoicePayment && (
                                                <input type='checkbox' onChange={handleSelectAll} checked={selectAll} />
                                            )}
                                        </th>
                                        <th>{resources.paymentText}</th>
                                        {containsValidCashDiscount && <th>{resources.discountText}</th>}
                                        <th>{resources.dueText}</th>
                                        <th>{resources.statusText}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {breakdown.map((br, idx) => {
                                        const allowableFreightCharge = getAllowableFreightAmount();
                                        allowableFreightChargeActual = br.AllowableFreightCharge || 0;
                                        const earliestInvoiceDate = getEarliestInvoiceDue()?.DueDate;
                                        const allowableFreightDueDate =
                                            earliestInvoiceDate &&
                                            new Date(earliestInvoiceDate.getFullYear(), earliestInvoiceDate?.getMonth(), 15);
                                        const isCheckboxEnabled = allowMultipleInvoicePayment || idx === 0 || allowableFreightChargeActual;
                                        const possibleCashDisc = containsValidCashDiscount && br.PossibleCashDisc ? br.PossibleCashDisc : 0;
                                        return (
                                            <React.Fragment key={idx}>
                                                {allowableFreightCharge && allowableFreightChargeActual ? (
                                                    <tr className='invoice-listview__freight-row' key={breakdown.length}>
                                                        <td className='checkbox'>
                                                            <input
                                                                type='checkbox'
                                                                checked={shouldSelectAllowableFreightRow}
                                                                disabled={disableAllowableFreightRow}
                                                                // onChange={() => {
                                                                //     handleRowSelect(breakdown.length);
                                                                //     // void addAllowableFreightAmountToCart(
                                                                //     //     invoice.id,
                                                                //     //     allowableFreightChargeActual,
                                                                //     //     !selectedRows[breakdown.length]
                                                                //     // );
                                                                // }}
                                                            />
                                                        </td>
                                                        <td>{formatCurrency(-allowableFreightChargeActual, invoice.currencyCode)}</td>
                                                        {containsValidCashDiscount && <td></td>}
                                                        {allowableFreightDueDate && (
                                                            <td className='freight-charge'>{formatDate(allowableFreightDueDate)}</td>
                                                        )}
                                                        <td>{resources.allowableFreightText}</td>
                                                    </tr>
                                                ) : (
                                                    undefined
                                                )}
                                                <tr>
                                                    <td className='checkbox'>
                                                        {isCheckboxEnabled ? (
                                                            <input
                                                                type='checkbox'
                                                                checked={selectedRows[idx]}
                                                                onChange={() => handleRowSelect(idx)}
                                                            />
                                                        ) : null}
                                                    </td>
                                                    <td>{formatCurrency(br.AmountCur || 0, invoice.currencyCode)}</td>
                                                    {containsValidCashDiscount && (
                                                        <td>
                                                            {possibleCashDisc && '-'}
                                                            {formatCurrency(possibleCashDisc || 0, invoice.currencyCode)}
                                                        </td>
                                                    )}
                                                    <td>{formatDate(convertUTCDateToLocalDate(br.DueDate!).toLocaleString())}</td>
                                                    <td
                                                        className={
                                                            isPastDue(br.DueDate)
                                                                ? 'ml-1 invoice-listview__tile--red invoice-listview__tile--bold'
                                                                : ''
                                                        }
                                                    >
                                                        {isPastDue(br.DueDate) ? (
                                                            <p className='breakdown-status-past-due'>{resources.pastDueText}</p>
                                                        ) : (
                                                            InvoicePaidStatusMap[invoice.status!]
                                                        )}
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                    {!isCreditNote && (
                        <>
                            <div className='payment-warnings'>
                                {Number(displayedAmount) !== Number(calculateTotalAmount(selectedRows).toFixed(2)) &&
                                    Number(displayedAmount) > 0 &&
                                    getAllowableFreightAmount() > 0 && (
                                        <div className='discount-warning'>{resources.freightDiscountWarningText}</div>
                                    )}
                                {isPaymentPending && <div className='payment-pending-message'>{paymentPendingMessage}</div>}
                            </div>
                            <div className='input'>
                                <div>
                                    <span className={'invoice-listview__tile--bold'}>{resources.payText}</span>

                                    <input
                                        className='amount-input'
                                        type='text'
                                        id='amountInput'
                                        value={`$${displayedAmount}`}
                                        onBlur={e => handleBlur(e.target.value)}
                                        onChange={e => {
                                            let value = e.target.value;
                                            value = value.replace('$', '');
                                            setDisplayedAmount(value);
                                            if (Number(value) < 0) {
                                                setTotalAmount(0);
                                                setDisplayedAmount('0.00');
                                                return;
                                            }
                                            if (Number(value) > calculateTotalAmount(selectedRows)) {
                                                setTotalAmount(calculateTotalAmount(selectedRows));
                                                !selectedRows.some(row => row)
                                                    ? setDisplayedAmount(calculateTotalAmount(selectedRows).toFixed(2))
                                                    : setDisplayedAmount(getDisplayedAmount(selectedRows));
                                                return;
                                            }
                                            setTotalAmount(value === '' ? null : Number(value));
                                        }}
                                    />
                                </div>
                                <button
                                    disabled={handleButtonDisabled()}
                                    onClick={() => {
                                        handleBlur(displayedAmount ?? '0.00');
                                        const cleanedAmount = Number(displayedAmount ?? '0.00');
                                        addInvoiceToCart(
                                            invoice.id,
                                            Number(cleanedAmount),
                                            allowableFreightChargeActual,
                                            shouldSelectAllowableFreightRow,
                                            invoice.amountDue!
                                        );
                                        // void addAllowableFreightAmountToCart(
                                        //     invoice.id,
                                        //     allowableFreightChargeActual,
                                        //     shouldSelectAllowableFreightRow
                                        // );
                                    }}
                                >
                                    {resources.addToCartText}
                                </button>
                            </div>
                            <div className='reset-amount-div'>
                                {totalAmount === calculateTotalAmount(selectedRows) &&
                                selectedRows.some(row => row) &&
                                Number(displayedAmount) !== 0 ? (
                                    <div className='specify-amount-text'>{resources.specifyAmountText}</div>
                                ) : totalAmount === calculateTotalAmount(selectedRows) &&
                                  !selectedRows.some(row => row) &&
                                  Number(displayedAmount) === 0 ? (
                                    <div className='specify-amount-text'>{resources.specifyAmountText}</div>
                                ) : (
                                    <div className='reset-amount-text' onClick={handleReset}>
                                        {resources.resetAmountText}
                                    </div>
                                )}
                                {shouldDisplayMessage() && <span className='payment-added-inline'>{paymentAddedMessage}</span>}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
